import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Ticket, Trash2, Pencil } from "lucide-react";

const EMPTY = {
  code: "",
  discount_type: "percent",
  value: "",
  min_order: 0,
  max_discount: "",
  expires_at: "",
  usage_limit: "",
  description: "",
  active: true,
};

export default function CouponsPanel() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/coupons");
      setCoupons(r.data);
    } catch (e) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      value: c.value,
      min_order: c.min_order || 0,
      max_discount: c.max_discount ?? "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
      usage_limit: c.usage_limit ?? "",
      description: c.description || "",
      active: c.active !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Code required"); return; }
    const value = parseFloat(form.value);
    if (isNaN(value) || value <= 0) { toast.error("Value must be positive"); return; }
    if (form.discount_type === "percent" && value > 100) { toast.error("Percent must be ≤ 100"); return; }
    setSaving(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        discount_type: form.discount_type,
        value,
        min_order: parseFloat(form.min_order) || 0,
        max_discount: form.max_discount === "" ? null : parseFloat(form.max_discount),
        expires_at: form.expires_at ? new Date(form.expires_at + "T23:59:59").toISOString() : null,
        usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit, 10),
        description: form.description || "",
        active: !!form.active,
      };
      if (editing) {
        await api.patch(`/admin/coupons/${editing.id}`, payload);
        toast.success("Coupon updated");
      } else {
        await api.post("/admin/coupons", payload);
        toast.success("Coupon created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("Coupon deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      toast.success(c.active ? "Coupon disabled" : "Coupon enabled");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading) return <div className="py-8 text-center text-[#6E675F]">Loading coupons...</div>;

  return (
    <div className="space-y-4" data-testid="coupons-panel">
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <p className="text-sm text-[#6E675F]">Create discount codes customers can apply at checkout.</p>
        <Button data-testid="add-coupon-btn" onClick={openAdd} className="btn-primary rounded-full">
          <Plus className="w-4 h-4 mr-2" /> New Coupon
        </Button>
      </div>

      {coupons.length === 0 ? (
        <div className="bg-white border border-[#E6DEC3] rounded-2xl p-12 text-center">
          <Ticket className="w-12 h-12 text-[#D9822B] mx-auto mb-3" />
          <p className="font-serif text-lg">No coupons yet</p>
          <p className="text-sm text-[#6E675F] mt-1">Create your first discount code — DIWALI20, FESTIVE10, WELCOME50, etc.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {coupons.map((c) => (
            <div key={c.id} data-testid={`coupon-${c.code}`} className={`bg-white border rounded-2xl p-5 ${c.active ? "border-[#E6DEC3]" : "border-[#D97706] opacity-70"}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-mono text-lg font-bold text-[#9E3B21]">{c.code}</p>
                    <span className="text-[10px] uppercase tracking-wider bg-[#F4EFE6] px-2 py-0.5 rounded-full font-semibold text-[#4A423C]">
                      {c.discount_type === "percent" ? `${c.value}% off` : `₹${c.value} off`}
                    </span>
                    {!c.active && <span className="text-[10px] uppercase tracking-wider bg-[#D97706]/20 text-[#D97706] px-2 py-0.5 rounded-full font-semibold">Inactive</span>}
                  </div>
                  {c.description && <p className="text-xs text-[#6E675F] mt-1">{c.description}</p>}
                </div>
                <Switch data-testid={`coupon-toggle-${c.code}`} checked={c.active} onCheckedChange={() => toggleActive(c)} />
              </div>
              <div className="text-xs text-[#6E675F] space-y-0.5 mb-3">
                {c.min_order ? <div>Min order: ₹{c.min_order}</div> : null}
                {c.max_discount ? <div>Max discount: ₹{c.max_discount}</div> : null}
                {c.expires_at ? <div>Expires: {new Date(c.expires_at).toLocaleDateString()}</div> : <div>No expiry</div>}
                {c.usage_limit ? <div>Uses: {c.usage_count} / {c.usage_limit}</div> : <div>Uses: {c.usage_count}</div>}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`edit-coupon-${c.code}`} onClick={() => openEdit(c)} size="sm" variant="outline" className="h-7 text-xs rounded-full">
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button data-testid={`delete-coupon-${c.code}`} onClick={() => del(c)} size="sm" variant="ghost" className="h-7 text-xs text-[#9E3B21]">
                  <Trash2 className="w-3 h-3 mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="coupon-form-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? `Edit ${editing.code}` : "New Coupon"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Coupon code *</Label>
              <Input data-testid="cf-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="DIWALI20" disabled={!!editing} className="mt-1.5 font-mono" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                <SelectTrigger data-testid="cf-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percent %</SelectItem>
                  <SelectItem value="flat">Flat ₹</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.discount_type === "percent" ? "Percent value" : "Flat value ₹"} *</Label>
              <Input data-testid="cf-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Min order ₹</Label>
              <Input data-testid="cf-min" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Max discount ₹ (percent only)</Label>
              <Input data-testid="cf-max" type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="No cap" className="mt-1.5" />
            </div>
            <div>
              <Label>Expires on</Label>
              <Input data-testid="cf-expires" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Usage limit</Label>
              <Input data-testid="cf-uselimit" type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="Unlimited" className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label>Description (shown internally)</Label>
              <Input data-testid="cf-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
            </div>
            <div className="col-span-2 flex items-center gap-3">
              <Switch data-testid="cf-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary" data-testid="cf-save">
              {saving ? "Saving..." : editing ? "Save" : "Create coupon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
